import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { authService } from '@/services/authService'
import { toast } from 'vue3-toastify'

export const useAuthStore = defineStore('auth', () => {
  const user = ref(null)
  const token = ref(localStorage.getItem('auth_token'))
  const loading = ref(false)
  const twoFactorRequired = ref(false)
  const tempToken = ref(null)
  const initialized = ref(false)

  // Computed
  const isAuthenticated = computed(() => !!token.value && !!user.value)
  const userRole = computed(() => user.value?.role)
  const isAdmin = computed(() => userRole.value === 'admin')
  const isKonselor = computed(() => userRole.value === 'konselor')
  const isMahasiswa = computed(() => userRole.value === 'mahasiswa')

  // Actions
  async function login(credentials) {
    try {
      loading.value = true
      const response = await authService.login(credentials)

      if (response.requires_2fa) {
        twoFactorRequired.value = true
        tempToken.value = response.temp_token
        toast.info('Kode 2FA telah dikirim ke email Anda')
        return { requires2FA: true }
      }

      await setAuthData(response)
      toast.success('Login berhasil!')
      return { success: true }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Login gagal')
      throw error
    } finally {
      loading.value = false
    }
  }

  async function verify2FA(code) {
    try {
      loading.value = true
      const response = await authService.verify2FA(tempToken.value, code)
      await setAuthData(response)
      
      twoFactorRequired.value = false
      tempToken.value = null
      toast.success('Verifikasi 2FA berhasil!')
      return true
    } catch (error) {
      toast.error(error.response?.data?.error || 'Verifikasi 2FA gagal')
      throw error
    } finally {
      loading.value = false
    }
  }

  async function register(userData) {
    try {
      loading.value = true
      await authService.register(userData)
      toast.success('Registrasi berhasil! Mengarahkan ke dashboard...')

      // Auto-login after registration using the same credentials
      await login({
        email: userData.email,
        password: userData.password
      })

      return true
    } catch (error) {
      toast.error(error.response?.data?.message || 'Registrasi gagal')
      throw error
    } finally {
      loading.value = false
    }
  }

  async function logout() {
    try {
      if (token.value) {
        await authService.logout()
      }
    } catch (error) {
      console.error('Logout error:', error)
    } finally {
      clearAuthData()
      toast.info('Anda telah logout')
    }
  }

  async function refreshToken() {
    try {
      const response = await authService.refresh()
      await setAuthData(response)
      return true
    } catch (error) {
      clearAuthData()
      return false
    }
  }

  async function checkAuth() {
    if (!token.value) {
      initialized.value = true
      return false
    }

    try {
      const userData = await authService.me()
      user.value = userData
      initialized.value = true
      return true
    } catch (error) {
      clearAuthData()
      initialized.value = true
      return false
    }
  }

  function setAuthData(response) {
    token.value = response.access_token
    user.value = response.user
    localStorage.setItem('auth_token', response.access_token)
    
    // Set token expiry for auto-refresh
    if (response.expires_in) {
      const expiryTime = Date.now() + (response.expires_in * 1000 * 0.9) // 90% of actual expiry
      localStorage.setItem('token_expiry', expiryTime.toString())
    }
  }

  function clearAuthData() {
    user.value = null
    token.value = null
    twoFactorRequired.value = false
    tempToken.value = null
    initialized.value = false
    localStorage.removeItem('auth_token')
    localStorage.removeItem('token_expiry')
  }

  // Auto-refresh token before expiry
  function setupTokenRefresh() {
    const expiryTime = localStorage.getItem('token_expiry')
    if (expiryTime) {
      const timeUntilRefresh = parseInt(expiryTime) - Date.now()
      if (timeUntilRefresh > 0) {
        setTimeout(() => {
          if (isAuthenticated.value) {
            refreshToken()
          }
        }, timeUntilRefresh)
      }
    }
  }

  // Check for role permissions
  function hasRole(requiredRole) {
    if (Array.isArray(requiredRole)) {
      return requiredRole.includes(userRole.value)
    }
    return userRole.value === requiredRole
  }

  function hasPermission(permission) {
    const rolePermissions = {
      admin: ['*'], // Admin has all permissions
      konselor: ['forms.read', 'forms.create', 'forms.edit', 'content.read', 'content.create', 'schedules.*', 'students.read'],
      mahasiswa: ['forms.read', 'forms.submit', 'content.read', 'schedules.read', 'recommendations.read']
    }

    const userPermissions = rolePermissions[userRole.value] || []
    
    return userPermissions.includes('*') || 
           userPermissions.includes(permission) ||
           userPermissions.some(p => p.endsWith('*') && permission.startsWith(p.slice(0, -1)))
  }

  // Initialize token refresh on store creation
  if (token.value) {
    setupTokenRefresh()
  }

  return {
    // State
    user,
    token,
    loading,
    twoFactorRequired,
    tempToken,
    initialized,

    // Computed
    isAuthenticated,
    userRole,
    isAdmin,
    isKonselor,
    isMahasiswa,

    // Actions
    login,
    verify2FA,
    register,
    logout,
    refreshToken,
    checkAuth,
    hasRole,
    hasPermission,
    setAuthData,
    clearAuthData
  }
})